import { type Observable, of, throwError } from 'rxjs';
import { ApiError } from '../http/api-error';
import { AuthApi, type SignInCredentials } from './auth-api';
import { HR_ADMIN, MOCK_PERSONAS, type Persona } from './mock-personas';
import { type SessionSnapshot } from './session.store';

/*
 * MOCK. Not a security boundary and not the real sign-in.
 *
 * Selected by `"mockApi": true` in `assets/config.json`, and refused in a production build.
 * It exists so the shell, guards and navigation can be developed and demonstrated before
 * (and without) a running API. Any login and password sign in; `ceo`, `admin`, `hr-executive`,
 * `team-lead`, `employee` and `operator` pick that persona and anything else gets HR Admin access
 * under the typed name.
 * It is kept in memory only, so a page reload signs the user out, as a lost cookie would.
 * Off by default (`mockApi: false` in `public/assets/config.json`); also the stand-in in unit specs.
 * Sign-in, session, permissions, data scope and password change are REAL when it is off. Every
 * feature page outside `core/` and `features/platform/account-*` and the sign-in page still renders
 * sample data from `features/demo-data`; each gets a generated client call in its own phase.
 * Delete this file once every screen runs against the real API.
 */

const COMPANY = { name: 'Tenels', timeZone: 'Asia/Dhaka' } as const;

/** Re-exported: the sign-in page builds its buttons from this. */
export { MOCK_PERSONAS };

function unauthorized(code: string): ApiError {
  return new ApiError({ status: 401, code });
}

export class InMemoryAuthApi extends AuthApi {
  private current: SessionSnapshot | null = null;

  /** Never fails: a login that is not a sample person signs in as an HR admin under that name. */
  signIn(credentials: SignInCredentials): Observable<SessionSnapshot> {
    const login = credentials.login.trim().toLowerCase();
    const known = MOCK_PERSONAS[login];
    const persona = known ?? {
      ...HR_ADMIN,
      displayName: credentials.login.trim() || 'User',
    };
    return of(this.start(login, persona));
  }

  /** Mock only: swaps the signed-in persona without a new sign-in. Unknown keys are refused. */
  viewAs(key: string): Observable<SessionSnapshot> {
    const persona = MOCK_PERSONAS[key];
    if (!persona) return throwError(() => unauthorized('auth.unknown_persona'));
    return of(this.start(key, persona));
  }

  private start(login: string, persona: Persona): SessionSnapshot {
    const snapshot: SessionSnapshot = {
      user: {
        id: `mock-${login}`,
        displayName: persona.displayName,
        mustChangePassword: false,
      },
      company: COMPANY,
      permissions: persona.permissions,
      roles: persona.roles,
      flags: persona.flags,
      dataScope: persona.dataScope,
    };
    this.current = snapshot;
    return snapshot;
  }

  me(): Observable<SessionSnapshot> {
    return this.current ? of(this.current) : throwError(() => unauthorized('auth.unauthenticated'));
  }

  refresh(): Observable<void> {
    return this.current ? of(undefined) : throwError(() => unauthorized('auth.session_revoked'));
  }

  signOut(): Observable<void> {
    this.current = null;
    return of(undefined);
  }

  changePassword(): Observable<void> {
    return of(undefined);
  }

  forgotPassword(): Observable<void> {
    return of(undefined);
  }

  resetPassword(): Observable<void> {
    return of(undefined);
  }
}
